/**
 * Mission plan math — derived from a mission's waypoints + bound dock.
 *
 * Used by:
 *   - MissionCostChips, to estimate planned duration without round-tripping
 *     telemetry from the operator.
 *   - KMZ export and other planning consumers that need the same leg
 *     sequence (pre-flight, takeoff, transit, user legs, return, landing).
 *
 * Pure / unit-testable — no React, Mapbox, or store dependencies.
 *
 * Telemetry during an actual flight (position, altitude, gimbal pose, etc.)
 * comes from the operator's webhooks, NOT from this file. The previous
 * synthesized-telemetry simulator was removed when the demo was retired.
 */

import type { DemoMission, DemoDockStation, DemoWaypoint, DemoLegParams } from '../components/dock-demo/types';
import { resolveEffectiveLegParams } from './missionValidation';

const EARTH_RADIUS_M = 6371000;

/** Phase the drone passes through on a planned mission. Used as a tag on
 *  each Leg so KMZ and other consumers can branch on it; the in-flight HUD
 *  reads phase from real telemetry, not from this. */
type MissionPhase =
  | 'Pre-flight'
  | 'Taking off'
  | 'Mission'
  | 'Hovering'
  | 'Photo'
  | 'Returning'
  | 'Landing';

/** Haversine distance in meters between two [lng, lat] points. */
function haversineM(a: [number, number], b: [number, number]): number {
  const lat1 = (a[1] * Math.PI) / 180;
  const lat2 = (b[1] * Math.PI) / 180;
  const dLat = ((b[1] - a[1]) * Math.PI) / 180;
  const dLng = ((b[0] - a[0]) * Math.PI) / 180;
  const x =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(x)));
}

function bearingDeg(a: [number, number], b: [number, number]): number {
  const lng1 = (a[0] * Math.PI) / 180;
  const lat1 = (a[1] * Math.PI) / 180;
  const lng2 = (b[0] * Math.PI) / 180;
  const lat2 = (b[1] * Math.PI) / 180;
  const y = Math.sin(lng2 - lng1) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(lng2 - lng1);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

/** Pause time in milliseconds for a given waypoint's action. */
function actionDelayMs(wp: DemoWaypoint): number {
  if (wp.action === 'hover') return Math.max(0, (wp.hoverSec ?? 2) * 1000);
  if (wp.action === 'photo') return 1500;
  return 0;
}

type RecordingLens = 'wide' | 'zoom' | 'ir';

/**
 * A single travel leg — straight-line move from `from` to `to`. Hover
 * segments are represented separately so we can hold position + alt while
 * the drone is "doing something" at a waypoint.
 */
interface LegState {
  /** True when video recording is active on this leg. Driven by leg params. */
  recording: boolean;
  /** Lens that's recording (only meaningful when recording is true). */
  recordingLens?: RecordingLens;
  /** Gimbal pitch held during the leg (absolute deg, -90..30). */
  gimbalPitchDeg: number;
  /** Gimbal yaw — undefined means "follow aircraft heading" (DJI default). */
  gimbalYawDeg?: number;
}

interface TravelLeg extends LegState {
  kind: 'travel';
  from: [number, number];
  to: [number, number];
  fromAlt: number;
  toAlt: number;
  speedMps: number;
  bearingDeg: number;
  distanceM: number;
  mode: MissionPhase;
  arrivingAtIndex?: number;
  /** Index in mission.waypoints for legs that connect two user waypoints. */
  userLegIndex?: number;
  intervalPhoto?: { distanceM: number; lenses: ReadonlyArray<RecordingLens> };
}

interface HoverLeg extends LegState {
  kind: 'hover';
  at: [number, number];
  altM: number;
  bearingDeg: number;
  durationMs: number;
  mode: MissionPhase;
  arrivingAtIndex: number;
  photoCaptureAtStart?: { id: string; lenses: ReadonlyArray<RecordingLens> };
}

type Leg = TravelLeg | HoverLeg;

interface LegPlan {
  legs: Leg[];
  /** Cumulative duration in ms at the END of each leg (same length as legs). */
  cumEndMs: number[];
  totalMs: number;
}

/**
 * Build the full leg sequence for a mission. The sequence is:
 *   1. Pre-flight pause (1 s) — drone sitting on the dock with mode 'Pre-flight'
 *   2. Vertical takeoff (3 s) — climb to first waypoint's altitude
 *   3. Outbound travel — dock home → wp[0]
 *   4. For each waypoint i: arrival hover/photo (action delay) + travel to wp[i+1]
 *   5. After last waypoint: arrival hover, then return travel home if
 *      finishAction === 'goHome'
 *   6. Landing (3 s) — vertical descent to ground
 */
function buildLegPlan(mission: DemoMission, dock: DemoDockStation): LegPlan {
  const home: [number, number] = [dock.lng, dock.lat];
  const wps = mission.waypoints;
  const globalSpeed = mission.globalSpeedMps;
  const legs: Leg[] = [];

  if (wps.length === 0) {
    return { legs: [], cumEndMs: [], totalMs: 0 };
  }

  let recording = false;
  let recordingLens: RecordingLens | undefined = undefined;
  let gimbalPitchDeg = -45;
  let gimbalYawDeg: number | undefined = undefined;

  const stateNow = (): LegState => ({
    recording,
    recordingLens,
    gimbalPitchDeg,
    gimbalYawDeg,
  });

  // 1. Pre-flight idle on the dock.
  legs.push({
    kind: 'hover',
    at: home,
    altM: 0,
    bearingDeg: 0,
    durationMs: 1000,
    mode: 'Pre-flight',
    arrivingAtIndex: 0,
    ...stateNow(),
  });

  // 2. Vertical takeoff to first waypoint's altitude.
  legs.push({
    kind: 'hover',
    at: home,
    altM: wps[0].alt,
    bearingDeg: bearingDeg(home, [wps[0].lng, wps[0].lat]),
    durationMs: 3000,
    mode: 'Taking off',
    arrivingAtIndex: 0,
    ...stateNow(),
  });

  // 3. Outbound travel to wp[0].
  {
    const to: [number, number] = [wps[0].lng, wps[0].lat];
    const speed = wps[0].speedMps ?? globalSpeed;
    legs.push({
      kind: 'travel',
      from: home,
      to,
      fromAlt: wps[0].alt,
      toAlt: wps[0].alt,
      speedMps: speed,
      bearingDeg: bearingDeg(home, to),
      distanceM: haversineM(home, to),
      mode: 'Mission',
      arrivingAtIndex: 1,
      ...stateNow(),
    });
  }

  // 4. Per-waypoint hover/photo + travel-to-next.
  for (let i = 0; i < wps.length; i++) {
    const w = wps[i];
    const next = wps[i + 1];
    const at: [number, number] = [w.lng, w.lat];
    const isLast = i === wps.length - 1;

    if (!isLast) {
      const lp: DemoLegParams = resolveEffectiveLegParams(wps, i);
      if (lp.gimbalPitch !== undefined) gimbalPitchDeg = lp.gimbalPitch;
      if (lp.gimbalYaw !== undefined) gimbalYawDeg = lp.gimbalYaw;
      const nextRecording = !!lp.recordVideo;
      if (nextRecording) {
        recording = true;
        recordingLens = lp.recordVideoLens ?? 'zoom';
      } else {
        recording = false;
        recordingLens = undefined;
      }
    } else {
      recording = false;
      recordingLens = undefined;
    }

    const actionMs = actionDelayMs(w);
    if (actionMs > 0) {
      const wpPhotoLenses: ReadonlyArray<RecordingLens> = w.action === 'photo'
        ? (w.photoPayload && w.photoPayload.length > 0 ? w.photoPayload : (['wide'] as RecordingLens[]))
        : [];
      legs.push({
        kind: 'hover',
        at,
        altM: w.alt,
        bearingDeg: w.heading ?? (next ? bearingDeg(at, [next.lng, next.lat]) : 0),
        durationMs: actionMs,
        mode: w.action === 'photo' ? 'Photo' : 'Hovering',
        arrivingAtIndex: i + 1,
        photoCaptureAtStart: w.action === 'photo'
          ? { id: `wp-${i}`, lenses: wpPhotoLenses }
          : undefined,
        ...stateNow(),
      });
    }

    if (next) {
      const to: [number, number] = [next.lng, next.lat];
      const speed = next.speedMps ?? globalSpeed;
      const lp = resolveEffectiveLegParams(wps, i);
      legs.push({
        kind: 'travel',
        from: at,
        to,
        fromAlt: w.alt,
        toAlt: next.alt,
        speedMps: speed,
        bearingDeg: bearingDeg(at, to),
        distanceM: haversineM(at, to),
        mode: 'Mission',
        arrivingAtIndex: i + 2,
        userLegIndex: i,
        intervalPhoto: lp.intervalPhoto?.enabled
          ? {
              distanceM: lp.intervalPhoto.distanceM,
              lenses: lp.intervalPhoto.lenses,
            }
          : undefined,
        ...stateNow(),
      });
    }
  }

  // 5. Return to home + landing if finishAction is 'goHome'.
  if (mission.finishAction === 'goHome') {
    const last = wps[wps.length - 1];
    const from: [number, number] = [last.lng, last.lat];
    const speed = mission.globalSpeedMps;
    legs.push({
      kind: 'travel',
      from,
      to: home,
      fromAlt: last.alt,
      toAlt: last.alt,
      speedMps: speed,
      bearingDeg: bearingDeg(from, home),
      distanceM: haversineM(from, home),
      mode: 'Returning',
      arrivingAtIndex: wps.length + 1,
      ...stateNow(),
    });

    legs.push({
      kind: 'hover',
      at: home,
      altM: 0,
      bearingDeg: bearingDeg(from, home),
      durationMs: 3000,
      mode: 'Landing',
      arrivingAtIndex: wps.length + 1,
      ...stateNow(),
    });
  }

  // Cumulative end times.
  const cumEndMs: number[] = [];
  let acc = 0;
  for (const leg of legs) {
    if (leg.kind === 'travel') {
      const safeSpeed = Math.max(0.5, leg.speedMps);
      acc += (leg.distanceM / safeSpeed) * 1000;
    } else {
      acc += leg.durationMs;
    }
    cumEndMs.push(acc);
  }

  return { legs, cumEndMs, totalMs: acc };
}

/** Planned mission duration in seconds. Read by MissionCostChips to show
 *  the estimated flight time before send-to-operator. */
export function getTotalDurationSec(mission: DemoMission, dock: DemoDockStation): number {
  return buildLegPlan(mission, dock).totalMs / 1000;
}
